/**
 * Recovery for "Failed to fetch dynamically imported module" errors.
 *
 * The SPA's loaded index.html references content-hashed chunks (e.g.
 * `assets/app-AppLayout-xdUqOOtd.js`). After a deploy those hashes change and
 * the old files are gone — served from an ephemeral ECS container with no
 * shared asset store. A tab open across a deploy, a stale cached index.html, or
 * an ALB routing a chunk request to a different-version task all produce a 404
 * on the import and surface the crash screen.
 *
 * A one-time full reload fetches a fresh index.html pointing at chunks that
 * actually exist, which deterministically recovers from every variant above.
 * The sessionStorage stamp bounds us to one reload per cooldown window so a
 * genuinely broken deploy (or a non-chunk error misclassified here) can't spin
 * the page in an infinite reload loop.
 */

const RELOAD_STAMP_KEY = "ls:chunk-reload-at";
const RELOAD_COOLDOWN_MS = 15_000;

/**
 * Heuristic match for the various browser phrasings of a failed dynamic import.
 * Chrome: "Failed to fetch dynamically imported module"
 * Firefox: "error loading dynamically imported module"
 * Safari: "Importing a module script failed"
 */
export function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";
  return /dynamically imported module|module script failed|error loading dynamically/i.test(
    message,
  );
}

/**
 * Reload the page once to pull a fresh index.html + current chunks. Returns
 * false (without reloading) if a reload already happened within the cooldown,
 * so callers can fall back to showing an error instead of looping.
 */
export function reloadOnceForStaleChunk(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_STAMP_KEY) ?? "0");
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    window.sessionStorage.setItem(RELOAD_STAMP_KEY, String(Date.now()));
  } catch {
    // sessionStorage can throw (private mode / disabled storage); reload anyway —
    // worst case is one extra reload, still bounded by the browser's own loop guard.
  }
  window.location.reload();
  return true;
}
